import { Auth } from 'aws-amplify';
import { toast } from 'react-toastify';
import { sanitizeFileName } from '../utils/operationHelpers';

interface SubmitFiles {
    contractFiles?: File[];
    artifactFiles?: File[];
}

interface SubmitOptions {
    successMessage?: string;
    onSuccess?: (data: any) => void;
    onError?: (errors: Array<{ fieldName: string; message: string }>) => void;
}

interface UseContractSubmitParams {
    apiUrl: string;
    apiKey: string;
    endpoint: string;
    setLoading: (v: boolean) => void;
}

const uploadFilesToS3 = async (uploadUrls: Record<string, any>, files: SubmitFiles) => {
    const uploadEntries = Object.entries(uploadUrls);
    if (uploadEntries.length === 0) return;

    toast.info(`Enviando ${uploadEntries.length} arquivo(s)...`);

    const allFiles = [...(files.contractFiles || []), ...(files.artifactFiles || [])];

    const uploadPromises = uploadEntries.map(async ([fileName, uploadInfo]: [string, any]) => {
        // Match pelo nome sanitizado em todos os arquivos
        let fileToUpload: File | undefined = allFiles.find(f => fileName.includes(sanitizeFileName(f.name)));

        // Fallback: contrato principal quando o nome não bateu com nenhum arquivo
        if (!fileToUpload && files.contractFiles?.length && allFiles.length === files.contractFiles.length) {
            fileToUpload = files.contractFiles[0];
        }

        if (!fileToUpload) return;

        const uploadResponse = await fetch(uploadInfo.upload_url, {
            method: 'PUT',
            body: fileToUpload,
            headers: { 'Content-Type': 'application/octet-stream' }
        });

        if (!uploadResponse.ok) {
            throw new Error(`Upload failed for ${fileName}`);
        }
    });

    await Promise.all(uploadPromises);
    toast.success('Arquivos enviados com sucesso!');
};

export const useContractSubmit = ({ apiUrl, apiKey, endpoint, setLoading }: UseContractSubmitParams) => {
    const submitContract = async (payload: any, files?: SubmitFiles, options?: SubmitOptions) => {
        try {
            setLoading(true);
            const token = (await Auth.currentSession()).getIdToken().getJwtToken();

            const response = await fetch(`${apiUrl}${endpoint}`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'x-api-key': apiKey,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                const data = await response.json();

                if (data.upload_urls && Object.keys(data.upload_urls).length > 0 && files) {
                    await uploadFilesToS3(data.upload_urls, files);
                }

                toast.success(options?.successMessage || 'Operação salva com sucesso!');
                options?.onSuccess?.(data);
                return { success: true, data };
            }

            const errorData: { message?: string; validationMessages?: Array<{ fieldName: string; code: string }> } = await response.json();
            toast.error(errorData.message || 'Erro ao salvar operação');

            const errors = errorData.validationMessages?.map(item => ({
                fieldName: item.fieldName,
                message: item.code
            })) || [];
            options?.onError?.(errors);
            return { success: false, errors };
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Erro ao conectar com o servidor';
            toast.error(message);
            return { success: false, errors: [] };
        } finally {
            setLoading(false);
        }
    };

    return { submitContract };
};
